import { APP_ENVIRONMENT } from "@solomon-apps/config";
import { useOrgAuthStore } from "@solomon-apps/shared";
import {
    Alert,
    AlertDescription,
    AlertTitle
} from "@solomon-apps/shared/src/components/ui/alert";
import { Badge } from "@solomon-apps/shared/src/components/ui/badge";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React from "react";
import { Navigate } from "react-router-dom";

import { CLIENT_HOME_PATH } from "@/constants/navigation";
import { useApiKeyPageWorkflow } from "@/hooks/useApiKeyPageWorkflow";
import type { ClientApiKeyMetadata } from "@/services/clientApiKeys";

import {
    CreateKeyDialog,
    RevokeKeyDialog,
    RotateKeyDialog,
    SecretDialog
} from "./ApiKeys/ApiKeyDialogs";
import { canRotateKey, formatApiKeyDate } from "./ApiKeys/apiKeyPageModel";
import { ClientPage } from "./shared/ClientPage";

export function ApiKeysPage(): React.ReactElement {
    const me = useOrgAuthStore((state) => state.me);
    const isOwner = me?.orgRole === "Owner";
    const {
        issuerClientUuid,
        query,
        queryErrorMessage,
        keys,
        selectedContext,
        activeCount,
        issuanceBlocked,
        rotationBlocked,
        issuedSecret,
        secret,
        issuanceWarning,
        createError,
        revokeError,
        rotateError,
        scopedRevokeTarget,
        scopedRotateTarget,
        isCreating,
        rotatingKeyIds,
        revokeMutation,
        createKey,
        rotateKey,
        revokeKey,
        discardSecret,
        openRevoke,
        closeRevoke,
        openRotate,
        closeRotate
    } = useApiKeyPageWorkflow(Boolean(isOwner));

    if (!isOwner) {
        return <Navigate to={CLIENT_HOME_PATH} replace />;
    }

    return (
        <ClientPage
            title="API keys"
            actions={
                <CreateKeyDialog
                    key={issuerClientUuid ?? "none"}
                    disabled={issuanceBlocked || isCreating}
                    error={createError}
                    isPending={isCreating}
                    onCreate={createKey}
                />
            }
        >
            <div className="grid gap-4 px-4 py-4 md:px-6 md:py-6">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line-base pb-4">
                    <div>
                        <Text variant="body" className="text-foreground-base">
                            Backend credentials
                        </Text>
                        <Text variant="small" className="text-foreground-muted">
                            {activeCount} of 5 active keys used in{" "}
                            {keys[0]?.environment ?? APP_ENVIRONMENT}
                        </Text>
                    </div>
                    <Button
                        size="sm"
                        variant="sunken"
                        disabled={query.isFetching}
                        onClick={() => void query.refetch()}
                    >
                        {query.isFetching ? "Refreshing…" : "Refresh"}
                    </Button>
                </div>

                {selectedContext?.readOnly ? (
                    <Alert variant="warning">
                        <AlertTitle>Key creation is blocked</AlertTitle>
                        <AlertDescription>
                            You can review and revoke existing keys while this
                            distributor relationship is read-only.
                        </AlertDescription>
                    </Alert>
                ) : null}
                {selectedContext && selectedContext.status !== "Active" ? (
                    <Alert variant="warning">
                        <AlertTitle>Key creation is blocked</AlertTitle>
                        <AlertDescription>
                            The distributor relationship must be active before
                            you can create a key. You can still review and
                            revoke existing keys.
                        </AlertDescription>
                    </Alert>
                ) : null}
                {activeCount >= 5 ? (
                    <Alert variant="warning">
                        <AlertTitle>Five-key limit reached</AlertTitle>
                        <AlertDescription>
                            Revoke an unused key before you create another key.
                        </AlertDescription>
                    </Alert>
                ) : null}
                {issuanceWarning ? (
                    <Alert variant="warning">
                        <AlertTitle>Secret response not received</AlertTitle>
                        <AlertDescription>{issuanceWarning}</AlertDescription>
                    </Alert>
                ) : null}
                {query.isError ? (
                    <Alert variant="destructive">
                        <AlertTitle>API keys could not load</AlertTitle>
                        <AlertDescription>{queryErrorMessage}</AlertDescription>
                    </Alert>
                ) : null}

                {query.isLoading ? (
                    <div role="status" className="py-12 text-center">
                        <Text variant="body" className="text-foreground-muted">
                            Loading API keys…
                        </Text>
                    </div>
                ) : !query.isError && keys.length === 0 ? (
                    <div className="border border-line-base bg-surface-sunken px-4 py-10 text-center">
                        <Text variant="body">No API keys</Text>
                        <Text
                            variant="small"
                            className="mt-1 text-foreground-muted"
                        >
                            Create a named key for your backend. The default is
                            no expiry.
                        </Text>
                    </div>
                ) : (
                    <div className="grid gap-3" aria-label="API keys">
                        {keys.map((key) => (
                            <ApiKeyRow
                                key={key.id}
                                apiKey={key}
                                onRevoke={() => openRevoke(key)}
                                onRotate={() => openRotate(key)}
                                rotateDisabled={
                                    rotationBlocked || !canRotateKey(key, keys)
                                }
                                isRotating={rotatingKeyIds.has(key.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
            <SecretDialog
                secret={secret}
                rotation={
                    issuedSecret?.contextUuid === issuerClientUuid
                        ? issuedSecret.rotation
                        : null
                }
                onDiscard={discardSecret}
            />
            <RotateKeyDialog
                apiKey={scopedRotateTarget}
                error={rotateError}
                isPending={
                    scopedRotateTarget
                        ? rotatingKeyIds.has(scopedRotateTarget.id)
                        : false
                }
                onClose={closeRotate}
                onRotate={rotateKey}
            />
            <RevokeKeyDialog
                apiKey={scopedRevokeTarget}
                error={revokeError}
                isPending={revokeMutation.isPending}
                onClose={closeRevoke}
                onRevoke={revokeKey}
            />
        </ClientPage>
    );
}

function ApiKeyRow({
    apiKey,
    onRevoke,
    onRotate,
    rotateDisabled,
    isRotating
}: {
    apiKey: ClientApiKeyMetadata;
    onRevoke: () => void;
    onRotate: () => void;
    rotateDisabled: boolean;
    isRotating: boolean;
}): React.ReactElement {
    const canRevoke =
        apiKey.status === "active" || apiKey.status === "rotating";
    return (
        <article className="grid gap-3 border border-line-base bg-surface-ground p-4 sm:grid-cols-2 xl:grid-cols-[minmax(12rem,1.2fr)_auto_repeat(5,minmax(7rem,0.8fr))_auto] xl:items-center">
            <div className="min-w-0">
                <Text variant="body" className="truncate">
                    {apiKey.label}
                </Text>
                <code className="block truncate text-xs text-foreground-muted">
                    {apiKey.keyPrefix}
                </code>
            </div>
            <div>
                <Badge
                    variant={
                        apiKey.status === "active"
                            ? "success"
                            : apiKey.status === "rotating"
                              ? "warning"
                              : "disabled"
                    }
                >
                    {apiKey.status}
                </Badge>
            </div>
            <KeyFact label="Creator" value={apiKey.createdByUuid.slice(0, 8)} />
            <KeyFact
                label="Created"
                value={formatApiKeyDate(apiKey.createdAt)}
            />
            <KeyFact
                label="Expires"
                value={formatApiKeyDate(apiKey.expiresAt)}
            />
            <KeyFact
                label="Last used"
                value={formatApiKeyDate(apiKey.lastUsedAt)}
            />
            <KeyFact
                label="Rotation cutoff"
                value={formatApiKeyDate(apiKey.rotationEndsAt)}
            />
            <div className="flex flex-wrap gap-2">
                <Button
                    size="sm"
                    variant="sunken"
                    disabled={rotateDisabled || isRotating}
                    onClick={onRotate}
                    aria-label={`Rotate ${apiKey.label}`}
                >
                    {isRotating ? "Rotating…" : "Rotate"}
                </Button>
                <Button
                    size="sm"
                    variant="sunken"
                    disabled={!canRevoke}
                    onClick={onRevoke}
                    aria-label={`Revoke ${apiKey.label}`}
                >
                    Revoke
                </Button>
            </div>
        </article>
    );
}

function KeyFact({
    label,
    value
}: {
    label: string;
    value: string;
}): React.ReactElement {
    return (
        <div className="min-w-0">
            <Text variant="fine-print" className="text-foreground-muted">
                {label}
            </Text>
            <Text variant="small" className="truncate text-foreground-base">
                {value}
            </Text>
        </div>
    );
}
